import { useState, useRef } from 'react';

import { Badge, Button, ButtonGroup, Label, Popover, PopoverContent, PopoverTrigger, Textarea } from '@@/ui/primitives';
import { XCircle, CheckIcon, CodeIcon, Play, Square, Loader2, DownloadIcon, ChevronDownIcon } from 'lucide-react';
import { cn } from '@@/lib/utils';
import { usePollySynthesize } from '../../../hooks/use-polly';
import { Engine } from '../../../api/api-tools';
import { LanguageCode, VoiceId } from '@aws-sdk/client-polly';
import { validateSSML } from '../utils/validate-ssml';

export interface PromptValue {
  content: string;
  kind: string;
}

interface PromptInputProps {
  value: PromptValue;
  onChange: (value: PromptValue) => void;
  disabled?: boolean;
  label?: string;
  fieldKey?: string;
}

interface SSMLTagVariation {
  label: string;
  tagTemplate: string;
  selfClosing?: boolean;
}

interface SSMLTagGroup {
  groupLabel: string;
  description: string;
  variations: SSMLTagVariation[];
}

const SSML_TAG_GROUPS: SSMLTagGroup[] = [
  {
    groupLabel: 'Root',
    description: 'The required root SSML tag.',
    variations: [{ label: 'Speak', tagTemplate: '<speak>text</speak>' }]
  },
  {
    groupLabel: 'Break',
    description: 'Adding a pause',
    variations: [
      { label: '1 second', tagTemplate: '<break time="1s"/>', selfClosing: true },
      { label: '2 seconds', tagTemplate: '<break time="2s"/>', selfClosing: true },
      { label: '500ms', tagTemplate: '<break time="500ms"/>', selfClosing: true }
    ]
  },
  {
    groupLabel: 'Prosody - Rate',
    description: 'Controlling volume, speaking rate, and pitch',
    variations: [
      { label: 'Slow', tagTemplate: '<prosody rate="slow">text</prosody>' },
      { label: 'Medium', tagTemplate: '<prosody rate="medium">text</prosody>' },
      { label: 'Fast', tagTemplate: '<prosody rate="fast">text</prosody>' }
    ]
  },
  {
    groupLabel: 'Prosody - Volume',
    description: 'Controlling volume, speaking rate, and pitch',
    variations: [
      { label: 'Soft', tagTemplate: '<prosody volume="soft">text</prosody>' },
      { label: 'Medium', tagTemplate: '<prosody volume="medium">text</prosody>' },
      { label: 'Loud', tagTemplate: '<prosody volume="loud">text</prosody>' }
    ]
  },
  {
    groupLabel: 'Prosody - Pitch',
    description: 'Controlling volume, speaking rate, and pitch',
    variations: [
      { label: 'Low', tagTemplate: '<prosody pitch="low">text</prosody>' },
      { label: 'Medium', tagTemplate: '<prosody pitch="medium">text</prosody>' },
      { label: 'High', tagTemplate: '<prosody pitch="high">text</prosody>' }
    ]
  },
  {
    groupLabel: 'Say-As',
    description: 'Controlling how special types of words are spoken',
    variations: [
      { label: 'Digits', tagTemplate: '<say-as interpret-as="digits">text</say-as>' },
      { label: 'Spell Out', tagTemplate: '<say-as interpret-as="spell-out">text</say-as>' },
      { label: 'Date', tagTemplate: '<say-as interpret-as="date">text</say-as>' }
    ]
  },
  {
    groupLabel: 'Language',
    description: 'Specifying another language for specific words',
    variations: [
      { label: 'Spanish', tagTemplate: '<lang xml:lang="es-ES">text</lang>' },
      { label: 'French', tagTemplate: '<lang xml:lang="fr-FR">text</lang>' },
      { label: 'German', tagTemplate: '<lang xml:lang="de-DE">text</lang>' }
    ]
  },
  {
    groupLabel: 'Structure',
    description: 'Adding pauses and structure',
    variations: [
      { label: 'Paragraph', tagTemplate: '<p>text</p>' },
      { label: 'Sentence', tagTemplate: '<s>text</s>' },
      { label: 'Speak', tagTemplate: '<speak>text</speak>' }
    ]
  },
  {
    groupLabel: 'Other',
    description: 'Additional SSML tags',
    variations: [
      { label: 'Phoneme', tagTemplate: '<phoneme alphabet="ipa" ph="">text</phoneme>' },
      { label: 'Substitute', tagTemplate: '<sub alias="">text</sub>' },
      { label: 'Word', tagTemplate: '<w role="amazon:VB">text</w>' },
      { label: 'Mark', tagTemplate: '<mark name=""/>', selfClosing: true }
    ]
  }
];

function PlayButton(props: { ssml: string }) {
  const synthesize = usePollySynthesize();
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }

    const validation = validateSSML(props.ssml);
    if (!validation.isValid) {
      return;
    }

    synthesize.mutate(
      {
        voiceId: VoiceId.Joanna,
        content: props.ssml,
        textType: 'ssml',
        languageCode: LanguageCode['en_US'],
        engine: Engine.NEURAL
      },
      {
        onSuccess: (data) => {
          const audio = new Audio(`data:${data.contentType};base64,${data.audioContent}`);
          audioRef.current = audio;

          audio.onended = () => {
            setIsPlaying(false);
            audioRef.current = null;
          };

          audio.onerror = () => {
            setIsPlaying(false);
            audioRef.current = null;
          };

          audio.play();
          setIsPlaying(true);
        },
        onError: (error) => {
          setIsPlaying(false);
          console.error('Failed to synthesize speech:', error);
        }
      }
    );
  };

  const handleDownload = () => {
    const validation = validateSSML(props.ssml);
    if (!validation.isValid) {
      return;
    }

    synthesize.mutate(
      {
        voiceId: VoiceId.Joanna,
        content: props.ssml,
        textType: 'ssml',
        languageCode: LanguageCode['en_US'],
        engine: Engine.NEURAL
      },
      {
        onSuccess: (data) => {
          const link = document.createElement('a');
          link.href = `data:${data.contentType};base64,${data.audioContent}`;
          link.download = 'synthesized-speech.mp3';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        onError: (error) => {
          console.error('Failed to synthesize speech for download:', error);
        }
      }
    );
  };

  const validation = validateSSML(props.ssml);
  const isDisabled = !validation.isValid || synthesize.isPending;

  return (
    <>
      <ButtonGroup>
        <Button
          onClick={handlePlay}
          disabled={isDisabled}
          type='button'
          variant='outline'
          size='sm'
          className='h-7 px-2 text-xs! font-medium! shadow-none!'
        >
          {synthesize.isPending ? (
            <>
              <Loader2 className='size-3 animate-spin' />
            </>
          ) : isPlaying ? (
            <>
              <Square className='size-3' />
              <span>Stop</span>
            </>
          ) : (
            <>
              <Play className='size-3' />
              <span>Play</span>
            </>
          )}
        </Button>
        <Button
          variant='outline'
          size='sm'
          className='h-7 px-2 text-xs! font-medium! shadow-none!'
          onClick={() => handleDownload()}
        >
          <DownloadIcon className='size-3' />
        </Button>
      </ButtonGroup>
    </>
  );
}

function TagSelector(props: { onInsert: (tagTemplate: string, selfClosing: boolean) => void }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (groupIndex: number, variationIndex: number) => {
    const group = SSML_TAG_GROUPS[groupIndex];
    const variation = group.variations[variationIndex];

    props.onInsert(variation.tagTemplate, variation.selfClosing ?? false);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='text-xs! font-medium! h-7! px-2! shadow-none!'>
          <CodeIcon className='size-3.5!' />
          <span>SSML</span>
          <ChevronDownIcon className='size-3.5!' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='font-sans w-80 p-3' align='center'>
        <div className='flex flex-col gap-4'>
          {SSML_TAG_GROUPS.map((group, groupIndex) => (
            <div key={groupIndex}>
              <div className='mb-2'>
                <div className='font-medium text-sm text-neutral-950'>{group.groupLabel}</div>
                <div className='text-xs! text-muted-foreground font-normal'>{group.description}</div>
              </div>
              <div className='grid grid-cols-3 gap-1'>
                {group.variations.map((variation, variationIndex) => (
                  <Button
                    variant='outline'
                    key={`${groupIndex}-${variationIndex}`}
                    onClick={() => handleSelect(groupIndex, variationIndex)}
                    className={cn(
                      'text-xs! shadow-none! font-medium! px-3! h-8! rounded-md!',
                      'hover:bg-neutral-100 truncate text-start! justify-start!'
                    )}
                  >
                    {variation.label}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Validation(props: { content: string }) {
  if (!props.content || props.content.trim() === '') {
    return null;
  }

  const validation = validateSSML(props.content);

  return (
    <div
      className={cn(
        'flex items-center gap-2 font-[450] py-2 px-2 -z-10!',
        'border-t border-t-neutral-300',
        validation.isValid ? 'bg-green-50' : 'bg-red-100'
      )}
    >
      {validation.isValid ? (
        <>
          <CheckIcon className='size-4 text-green-900' />
          <span className='text-xs text-green-900'>SSML is validated</span>
        </>
      ) : (
        <div className='flex items-center text-red-900 gap-3'>
          <XCircle className='size-4' />
          <div className='flex flex-col'>
            {validation.errors.map((error: string, idx: number) => (
              <span key={idx} className='text-xs'>
                {error}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PromptInput(props: PromptInputProps) {
  const { value, onChange, disabled = false, label, fieldKey } = props;

  const [selection, setSelection] = useState({ start: 0, end: 0 });

  const handleInsertTag = (tagTemplate: string, selfClosing: boolean) => {
    const currentContent = value.content || '';
    const { start, end } = selection;
    const selectedText = currentContent.substring(start, end);

    let newContent: string;

    if (selfClosing) {
      newContent = currentContent.substring(0, start) + tagTemplate + currentContent.substring(end);
    } else {
      if (selectedText) {
        newContent =
          currentContent.substring(0, start) +
          tagTemplate.replace('text', selectedText) +
          currentContent.substring(end);
      } else {
        newContent = currentContent.substring(0, start) + tagTemplate + currentContent.substring(end);
      }
    }

    onChange({ ...value, content: newContent });
  };

  const handleSelectionChange = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    setSelection({
      start: target.selectionStart,
      end: target.selectionEnd
    });
  };

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          {label && <Label className='text-neutral-800 text-sm font-medium'>{label}</Label>}
          <Badge variant='outline' className='text-slate-800 border-slate-400 text-xs!'>
            Prompt
          </Badge>
        </div>
        <div className='flex items-center gap-2'>
          <TagSelector onInsert={handleInsertTag} />
          {value.content && <PlayButton ssml={value.content} />}
        </div>
      </div>
      <div className='relative border border-neutral-300 rounded-md! overflow-auto shadow-xs'>
        <Textarea
          value={value.content || ''}
          onChange={(e) => onChange({ ...value, content: e.target.value })}
          onSelect={handleSelectionChange}
          placeholder={
            `Enter SSML content${fieldKey ? ` for ${fieldKey}` : ''}... ` + `(e.g., <speak>Hello world</speak>)`
          }
          className={cn(
            'text-sm! font-medium! bg-white! w-full min-h-[100px] font-mono! z-99!',
            'rounded-none! border-none!'
          )}
          rows={4}
          disabled={disabled}
        />
        {value.content && <Validation content={value.content} />}
      </div>
    </div>
  );
}
